import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';
import { useLocationStore } from '@/store/location';
import { subscribeToRawInventory, unsubscribe } from '@/lib/realtime';

export interface RawInventoryItem {
  id: string;
  organization_id: string;
  name: string;
  sku: string | null;
  barcode: string | null;
  description: string | null;
  unit_of_measure: string;
  cost_per_unit: number | null;
  supplier_id: string | null;
  supplier_item_code: string | null;
  category: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  supplier?: { id: string; name: string } | null;
}

export interface RawInventoryStock {
  id: string;
  inventory_item_id: string;
  location_id: string;
  quantity_on_hand: number;
  quantity_reserved: number;
  reorder_point: number | null;
  reorder_quantity: number | null;
  last_counted_at: string | null;
  last_counted_by: string | null;
  created_at: string;
  updated_at: string;
  item?: RawInventoryItem;
  location?: { id: string; name: string };
}

export interface RawInventoryTransaction {
  id: string;
  inventory_item_id: string;
  location_id: string;
  transaction_type: 'received' | 'adjustment' | 'count' | 'consumed' | 'waste' | 'transfer_out' | 'transfer_in';
  quantity_change: number;
  quantity_before: number | null;
  quantity_after: number | null;
  reference_type: string | null;
  reference_id: string | null;
  notes: string | null;
  performed_by: string | null;
  created_at: string;
  user?: { first_name: string; last_name: string } | null;
}

export interface CreateInventoryItemInput {
  name: string;
  sku?: string;
  barcode?: string;
  description?: string;
  unit_of_measure?: string;
  cost_per_unit?: number;
  supplier_id?: string;
  supplier_item_code?: string;
  category?: string;
}

export interface UpdateInventoryItemInput {
  name?: string;
  sku?: string;
  barcode?: string;
  description?: string;
  unit_of_measure?: string;
  cost_per_unit?: number;
  supplier_id?: string | null;
  supplier_item_code?: string;
  category?: string;
  is_active?: boolean;
}

const UNIT_OPTIONS = [
  { value: 'each', label: 'Each' },
  { value: 'pieces', label: 'Pieces' },
  { value: 'kg', label: 'Kilogram (kg)' },
  { value: 'g', label: 'Gram (g)' },
  { value: 'lb', label: 'Pound (lb)' },
  { value: 'oz', label: 'Ounce (oz)' },
  { value: 'liter', label: 'Liter (L)' },
  { value: 'ml', label: 'Milliliter (mL)' },
  { value: 'gallon', label: 'Gallon' },
  { value: 'case', label: 'Case' },
  { value: 'box', label: 'Box' },
  { value: 'bag', label: 'Bag' },
  { value: 'roll', label: 'Roll' },
];

const CATEGORY_OPTIONS = [
  { value: 'raw_ingredient', label: 'Raw Ingredient' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'supplies', label: 'Supplies' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'other', label: 'Other' },
];

export interface UseInventoryItemsOptions {
  /**
   * When true, this hook will respect the global `locationScope === 'all'` setting
   * and fetch raw inventory stock across the organization.
   */
  allowAllLocations?: boolean;
  /**
   * When true, inactive (soft-deleted) items are included as well. Used by the
   * admin Raw Inventory screen so deactivated items that are still referenced by
   * composites can be found and reactivated. Other consumers keep getting only
   * active items, and the query keys are kept separate so the caches don't collide.
   */
  includeInactive?: boolean;
}

export function useInventoryItems(options: UseInventoryItemsOptions = {}) {
  const { organization, user } = useAuth();
  const { currentLocation, locationScope } = useLocationStore();
  const queryClient = useQueryClient();
  const organizationId = organization?.id;
  const locationId = currentLocation?.id;
  const viewingAllLocations = !!options.allowAllLocations && locationScope === 'all';
  const includeInactive = !!options.includeInactive;

  // Fetch all inventory items
  const {
    data: items,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['raw-inventory-items', organizationId, includeInactive ? 'all-statuses' : 'active'],
    queryFn: async () => {
      if (!organizationId) return [];

      let query = supabase
        .from('raw_inventory_items')
        .select(`
          *,
          supplier:suppliers(id, name)
        `)
        .eq('organization_id', organizationId);

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query.order('name');

      if (error) throw error;
      return data as RawInventoryItem[];
    },
    enabled: !!organizationId,
  });

  // Fetch inventory stock for current location
  const {
    data: stock,
    isLoading: stockLoading,
    refetch: refetchStock,
  } = useQuery({
    queryKey: ['raw-inventory-stock', organizationId, viewingAllLocations ? 'all' : locationId, includeInactive ? 'all-statuses' : 'active'],
    queryFn: async () => {
      if (viewingAllLocations) {
        if (!organizationId) return [];

        let query = supabase
          .from('raw_inventory_stock')
          .select(`
            *,
            item:raw_inventory_items!inner(*),
            location:locations(id, name)
          `)
          .eq('item.organization_id', organizationId);

        if (!includeInactive) {
          query = query.eq('item.is_active', true);
        }

        const { data, error } = await query;

        if (error) throw error;
        return data as RawInventoryStock[];
      }

      if (!locationId) return [];

      const { data, error } = await supabase
        .from('raw_inventory_stock')
        .select(`
          *,
          item:raw_inventory_items(*)
        `)
        .eq('location_id', locationId);

      if (error) throw error;
      return data as RawInventoryStock[];
    },
    enabled: viewingAllLocations ? !!organizationId : !!locationId,
  });

  // Subscribe to real-time raw inventory updates
  useEffect(() => {
    if (!organizationId) return;
    const channel = subscribeToRawInventory(organizationId, () => {
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-items'] });
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-stock'] });
    });
    return () => { unsubscribe(channel); };
  }, [organizationId, queryClient]);

  // Fetch transactions for an item
  const fetchTransactions = async (itemId: string, locationId?: string) => {
    let query = supabase
      .from('raw_inventory_transactions')
      .select(`
        *,
        user:profiles!raw_inventory_transactions_performed_by_fkey(first_name, last_name)
      `)
      .eq('inventory_item_id', itemId)
      .order('created_at', { ascending: false })
      .limit(100);

    if (locationId) {
      query = query.eq('location_id', locationId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as RawInventoryTransaction[];
  };

  // Create inventory item
  const createItem = useMutation({
    mutationFn: async (input: CreateInventoryItemInput) => {
      if (!organizationId) throw new Error('No organization');

      const { data, error } = await supabase
        .from('raw_inventory_items')
        .insert({
          organization_id: organizationId,
          name: input.name,
          sku: input.sku || null,
          barcode: input.barcode || null,
          description: input.description || null,
          unit_of_measure: input.unit_of_measure || 'each',
          cost_per_unit: input.cost_per_unit || null,
          supplier_id: input.supplier_id || null,
          supplier_item_code: input.supplier_item_code || null,
          category: input.category || null,
          created_by: user?.id,
        })
        .select(`*, supplier:suppliers(id, name)`)
        .single();

      if (error) throw error;
      return data as RawInventoryItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-items', organizationId] });
    },
  });

  // Update inventory item
  const updateItem = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateInventoryItemInput }) => {
      const { data, error } = await supabase
        .from('raw_inventory_items')
        .update(updates)
        .eq('id', id)
        .select(`*, supplier:suppliers(id, name)`)
        .single();

      if (error) throw error;
      return data as RawInventoryItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-items', organizationId] });
    },
  });

  // Delete inventory item (soft delete)
  const deleteItem = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('raw_inventory_items')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-items', organizationId] });
    },
  });

  // Adjust inventory
  const adjustInventory = useMutation({
    mutationFn: async ({
      itemId,
      locationId,
      quantityChange,
      transactionType,
      notes,
    }: {
      itemId: string;
      locationId: string;
      quantityChange: number;
      transactionType: string;
      notes?: string;
    }) => {
      const { data, error } = await supabase.rpc('adjust_raw_inventory', {
        p_item_id: itemId,
        p_location_id: locationId,
        p_quantity_change: quantityChange,
        p_transaction_type: transactionType,
        p_notes: notes || null,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-stock'] });
    },
  });

  // Update stock settings (reorder point, etc.)
  const updateStockSettings = useMutation({
    mutationFn: async ({
      itemId,
      locationId,
      reorderPoint,
      reorderQuantity,
    }: {
      itemId: string;
      locationId: string;
      reorderPoint?: number | null;
      reorderQuantity?: number | null;
    }) => {
      // Ensure stock record exists
      await supabase
        .from('raw_inventory_stock')
        .upsert({
          inventory_item_id: itemId,
          location_id: locationId,
          reorder_point: reorderPoint,
          reorder_quantity: reorderQuantity,
        }, {
          onConflict: 'inventory_item_id,location_id',
        });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-stock'] });
    },
  });

  // Get stock for a specific item at current location
  const getItemStock = (itemId: string): RawInventoryStock | undefined => {
    return stock?.find(s => s.inventory_item_id === itemId);
  };

  // Get all stock records for an item (used for all-locations views)
  const getItemStocks = (itemId: string): RawInventoryStock[] => {
    return (stock || []).filter(s => s.inventory_item_id === itemId);
  };

  // Get total on hand across all locations for an item
  const getItemTotalOnHand = (itemId: string): number => {
    return getItemStocks(itemId).reduce((sum, s) => sum + (s.quantity_on_hand || 0), 0);
  };

  // Get low stock items
  const lowStockItems = (stock || []).filter(s => 
    s.reorder_point && s.quantity_on_hand <= s.reorder_point
  );

  // Get out of stock items
  const outOfStockItems = (stock || []).filter(s => s.quantity_on_hand <= 0);

  return {
    // Data
    items: items || [],
    stock: stock || [],
    lowStockItems,
    outOfStockItems,
    
    // Loading states
    isLoading,
    stockLoading,
    error,
    
    // Mutations
    createItem,
    updateItem,
    deleteItem,
    adjustInventory,
    updateStockSettings,
    
    // Helpers
    fetchTransactions,
    getItemStock,
    getItemStocks,
    getItemTotalOnHand,
    refetch,
    refetchStock,
    
    // Constants
    unitOptions: UNIT_OPTIONS,
    categoryOptions: CATEGORY_OPTIONS,
    
    // Context
    organizationId,
    locationId,
    viewingAllLocations,
  };
}

















